import React, { useState, useRef } from 'react';
import { User, Loader, Camera } from 'lucide-react';
import BaseModal from './BaseModal';
import { LoadingButton } from './LoadingStates';
import { sanitizeText } from '../utils/security';
import { VALIDATION_RULES, CSS_CLASSES } from '../utils/constants';

interface ProfileCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: any;
  supabase: any;
  onProfileCompleted: (profile: any) => void;
}

const ProfileCompletionModal: React.FC<ProfileCompletionModalProps> = ({
  isOpen,
  onClose,
  currentUser,
  supabase,
  onProfileCompleted,
}) => {
  const [formData, setFormData] = useState({
    username: '',
    bio: '',
    location: '',
    avatar_url: '',
    seller_terms: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [usernameStatus, setUsernameStatus] = useState(''); // 'checking', 'available', 'taken', 'invalid'
  const fileInputRef = useRef<HTMLInputElement>(null);


  // Check username availability
  const checkUsername = async (username: string) => {
    if (!username || username.length < 3) {
      setUsernameStatus('');
      return;
    }

    // Validate format
    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      setUsernameStatus('invalid');
      setErrors(prev => ({
        ...prev,
        username: 'Username can only contain letters, numbers, hyphens, and underscores',
      }));
      return;
    }

    setUsernameStatus('checking');
    setErrors(prev => ({ ...prev, username: '' }));

    try {
      const { data, error } = await supabase.rpc('is_username_available', {
        check_username: username,
      });

      if (error) {
        console.error('Username check error:', error);
        setUsernameStatus('');
        return;
      }

      if (data) {
        setUsernameStatus('available');
      } else {
        setUsernameStatus('taken');
        setErrors(prev => ({ ...prev, username: 'Username is already taken' }));
      }
    } catch (err) {
      console.error('Username check failed:', err);
      setUsernameStatus('');
    }
  };

  // Debounced username check
  React.useEffect(() => {
    if (formData.username && formData.username.length >= 3) {
      const timer = setTimeout(() => {
        checkUsername(formData.username);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setUsernameStatus('');
    }
  }, [formData.username]);

  // Get username validation icon
  const getUsernameIcon = () => {
    switch (usernameStatus) {
      case 'checking':
        return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>;
      case 'available':
        return <User className="h-4 w-4 text-green-500" />;
      case 'taken':
        return <User className="h-4 w-4 text-red-500" />;
      case 'invalid':
        return <User className="h-4 w-4 text-red-500" />;
      default:
        return <User className="h-4 w-4 text-gray-400" />;
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, image: 'Please select an image file' }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, image: 'Image must be less than 5MB' }));
      return;
    }

    setUploadingImage(true);
    setErrors(prev => ({ ...prev, image: '' }));

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${currentUser.id}_${Date.now()}.${fileExt}`;
      const filePath = `profile-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('media-images')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: urlData } = supabase.storage.from('media-images').getPublicUrl(filePath);
      const profileImageUrl = urlData.publicUrl;

      setFormData(prev => ({ ...prev, avatar_url: profileImageUrl }));
    } catch (err) {
      console.error('Error uploading profile image:', err);
      setErrors(prev => ({ ...prev, image: 'Failed to upload image. Please try again.' }));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);
    setErrors({});

    // Validate required fields
    if (!formData.username) {
      setErrors({ username: 'Username is required' });
      setIsSubmitting(false);
      return;
    }

    if (usernameStatus !== 'available') {
      setErrors({ username: 'Please choose a valid, available username' });
      setIsSubmitting(false);
      return;
    }

    try {
      // First, check if the profile still has a temporary username
      // This prevents race conditions when multiple tabs are open
      const { data: currentProfile, error: checkError } = await supabase
        .from('user_profiles')
        .select('username')
        .eq('user_id', currentUser.id)
        .single();

      if (checkError) {
        setErrors({ general: 'Failed to verify profile status: ' + checkError.message });
        return;
      }

      // If username is no longer temporary, someone else already completed the profile
      if (!currentProfile.username?.startsWith('user_')) {
        setErrors({ general: 'Profile has already been completed in another tab. Please refresh the page.' });
        return;
      }

      const profileData = {
        username: formData.username.toLowerCase(),
        bio: formData.bio || null,
        location: formData.location || null,
        avatar_url: formData.avatar_url || null,
        seller_terms: 'Contact seller for terms',
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('user_profiles')
        .update(profileData)
        .eq('user_id', currentUser.id)
        .select()
        .single();

      if (error) {
        setErrors({ general: 'Failed to update profile: ' + error.message });
        return;
      }

      console.log('Profile completed successfully:', data);
      onProfileCompleted(data);
      onClose();
      
      // Refresh the page to load the updated username throughout the app
      window.location.reload();
    } catch (err) {
      console.error('Profile completion error:', err);
      setErrors({ general: 'An unexpected error occurred' });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <BaseModal
      isOpen={isOpen}
      onClose={() => {}} // Prevent closing until profile is completed
      title="Complete Your Profile"
      maxWidth="md"
      showCloseButton={false}
    >
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <div
            className="relative w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center border-2 border-gray-600 cursor-pointer hover:border-red-500 transition-colors overflow-hidden group"
            onClick={handleAvatarClick}
          >
            {formData.avatar_url ? (
              <img
                src={formData.avatar_url}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <User size={32} className="text-gray-300" />
            )}
            {uploadingImage && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <Loader className="animate-spin text-white" size={20} />
              </div>
            )}
            {!uploadingImage && (
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center transition-all">
                <Camera
                  size={20}
                  className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </div>
            )}
          </div>
        </div>
        <p className="text-gray-300">
          Welcome! Complete your profile to get the most out of Rewind Archive. You can always update this later.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />

        {errors.image && (
          <div className="text-center">
            <p className="text-red-500 text-sm">{errors.image}</p>
          </div>
        )}

        <div>
          <div className="relative">
            <input
              type="text"
              value={formData.username}
              onChange={e => {
                const sanitizedUsername = sanitizeText(e.target.value).substring(0, 30);
                setFormData({ ...formData, username: sanitizedUsername });
                setErrors(prev => ({ ...prev, username: '' }));
              }}
              className={`${CSS_CLASSES.INPUT_STANDARD} bg-black text-white border-gray-600 hover:border-red-500 placeholder-gray-400 pr-10 ${
                errors.username ? 'border-red-500' : ''
              }`}
              placeholder="Choose your username"
              maxLength={30}
              required
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              {getUsernameIcon()}
            </div>
          </div>
          {errors.username && <p className="text-red-500 text-sm mt-1">{errors.username}</p>}
          {usernameStatus === 'available' && (
            <p className="text-green-500 text-sm mt-1">Username is available!</p>
          )}
          <p className="text-gray-400 text-xs mt-1">
            3-30 characters, letters, numbers, hyphens, and underscores only
          </p>
        </div>

        <div>
          <textarea
            value={formData.bio}
            onChange={e => {
              const sanitizedBio = sanitizeText(e.target.value).substring(0, VALIDATION_RULES.BIO_MAX_LENGTH);
              setFormData({ ...formData, bio: sanitizedBio });
            }}
            className={`${CSS_CLASSES.INPUT_STANDARD} bg-black text-white border-gray-600 hover:border-red-500 placeholder-gray-400`}
            placeholder="Bio"
            rows={3}
            maxLength={VALIDATION_RULES.BIO_MAX_LENGTH}
          />
          <p className="text-gray-400 text-xs mt-1">{formData.bio.length}/{VALIDATION_RULES.BIO_MAX_LENGTH} characters</p>
        </div>

        <div>
          <input
            type="text"
            value={formData.location}
            onChange={e => {
              const sanitizedLocation = sanitizeText(e.target.value).substring(0, VALIDATION_RULES.LOCATION_MAX_LENGTH);
              setFormData({ ...formData, location: sanitizedLocation });
            }}
            className={`${CSS_CLASSES.INPUT_STANDARD} bg-black text-white border-gray-600 hover:border-red-500 placeholder-gray-400`}
            placeholder="Location"
            maxLength={VALIDATION_RULES.LOCATION_MAX_LENGTH}
          />
        </div>


        {errors.general && (
          <div className="bg-red-900 border border-red-500 rounded-lg p-3">
            <p className="text-red-300 text-sm">{errors.general}</p>
            {errors.general.includes('already been completed') && (
              <button
                onClick={() => window.location.reload()}
                className="mt-2 px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
              >
                Refresh Page
              </button>
            )}
          </div>
        )}

        <div className="flex justify-end mt-6">
          <LoadingButton
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting || !formData.username || usernameStatus !== 'available'}
            className={CSS_CLASSES.BUTTON_PRIMARY}
          >
            Complete Profile
          </LoadingButton>
        </div>
      </form>
    </BaseModal>
  );
};

export default ProfileCompletionModal;